// ═══════════════════════════════════════════════════════════════
// VOLUNTEEROS — Dashboard
// Overview stats, recent assignments, active incidents, quick actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, SimpleGrid, Group, Badge, Button,
  Loader, Center, Table, Stack, CopyButton, ActionIcon, Tooltip, Code,
} from '@mantine/core';
import {
  IconPlus, IconArrowRight, IconCalendarEvent, IconLink, IconCopy, IconCheck,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import type { VolAssignment } from '../../store/volunteerStore';
import { primary } from '../../lib/colors';

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'blue',
  confirmed: 'green',
  completed: 'teal',
  'no-show': 'red',
  cancelled: 'gray',
  declined: 'orange',
};

export default function VolunteerDashboard() {
  const navigate = useNavigate();
  const { volunteers, events, assignments, loadVolunteers, loadEvents, loadAssignments } = useVolunteerStore();
  const { activeIncident } = useAuthStore();

  const [initialLoad, setInitialLoad] = useState(true);

  const fetchData = useCallback(async () => {
    await Promise.all([loadVolunteers(), loadEvents(), loadAssignments()]);
  }, [loadVolunteers, loadEvents, loadAssignments]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // ── Computed data ──────────────────────────────────────────

  const totalVolunteers = volunteers.length;
  const activeVolunteers = useMemo(
    () => volunteers.filter(v => v.status === 'active').length,
    [volunteers],
  );
  const activeEvents = useMemo(
    () => events.filter(e => e.status === 'active').length,
    [events],
  );

  const now = dayjs();
  const upcomingShifts = useMemo(
    () => assignments.filter(a =>
      (a.status === 'scheduled' || a.status === 'confirmed') &&
      a.shiftDate && dayjs(a.shiftDate).isAfter(now, 'day'),
    ).length,
    [assignments, now],
  );

  // Recent 10 assignments by datecreated
  const recentAssignments = useMemo(() =>
    [...assignments]
      .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''))
      .slice(0, 10),
    [assignments],
  );

  // Helper lookups
  const volunteerName = (volId: string) => {
    const v = volunteers.find(vol => String(vol.id) === String(volId));
    return v ? `${v.firstName} ${v.lastName}`.trim() || 'Unknown' : 'Unknown';
  };
  const eventName = (evId: string) => {
    const e = events.find(ev => String(ev.id) === String(evId));
    return e?.name || 'Unknown';
  };

  // ── Render ─────────────────────────────────────────────────

  if (initialLoad) {
    return <Center h={400}><Loader color="blue" /></Center>;
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">VolunteerOS Dashboard</Title>
          <Text size="sm" c="dimmed">
            {activeIncident
              ? `Active incident: ${activeIncident.name}`
              : `${totalVolunteers} volunteers across ${events.length} events`}
          </Text>
        </div>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconCalendarEvent size={14} />}
            onClick={() => navigate('/volunteer/events')}
          >
            Create Event
          </Button>
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/volunteer/roster')}
          >
            Add Volunteer
          </Button>
        </Group>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }} onClick={() => navigate('/volunteer/roster')}>
          <Text size="xs" c="dimmed">Total Volunteers</Text>
          <Text fz={28} fw={700}>{totalVolunteers}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }} onClick={() => navigate('/volunteer/roster')}>
          <Text size="xs" c="dimmed">Active</Text>
          <Text fz={28} fw={700} c="#07963F">{activeVolunteers}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }} onClick={() => navigate('/volunteer/events')}>
          <Text size="xs" c="dimmed">Events</Text>
          <Text fz={28} fw={700} c={primary[700]}>{activeEvents}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ cursor: 'pointer' }} onClick={() => navigate('/volunteer/schedule')}>
          <Text size="xs" c="dimmed">Upcoming Shifts</Text>
          <Text fz={28} fw={700} c="#A67103">{upcomingShifts}</Text>
        </Paper>
      </SimpleGrid>

      {/* Public Registration Link */}
      <Paper p="sm" radius="sm" withBorder mb="md" style={{ borderLeft: '3px solid var(--mantine-color-teal-5)' }}>
        <Group justify="space-between">
          <Group gap="sm">
            <IconLink size={16} color="var(--mantine-color-teal-6)" />
            <div>
              <Text size="sm" fw={500}>Public Volunteer Registration</Text>
              <Text size="xs" c="dimmed">Share this link for volunteers to self-register</Text>
            </div>
          </Group>
          <Group gap="xs">
            <Code style={{ fontSize: 11 }}>{`${window.location.origin}/volunteer/signup${activeIncident ? `/${activeIncident.id}` : ''}`}</Code>
            <CopyButton value={`${window.location.origin}/volunteer/signup${activeIncident ? `/${activeIncident.id}` : ''}`}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy link'}>
                  <ActionIcon size="sm" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy}>
                    {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
            <Button size="compact-xs" variant="light" color="teal"
              onClick={() => window.open(`/volunteer/signup${activeIncident ? `/${activeIncident.id}` : ''}`, '_blank')}>
              Open
            </Button>
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Recent Assignments */}
        <Paper p="md" radius="sm" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Recent Assignments</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/volunteer/schedule')}
            >
              View All
            </Button>
          </Group>
          {recentAssignments.length === 0 ? (
            <Stack align="center" gap="xs" py="md">
              <Text size="sm" c="dimmed">No assignments yet.</Text>
              <Button size="xs" variant="light" onClick={() => navigate('/volunteer/schedule')}>
                Schedule Shifts
              </Button>
            </Stack>
          ) : (
            <Table.ScrollContainer minWidth={400}>
              <Table verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Volunteer</Table.Th>
                    <Table.Th>Event</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {recentAssignments.map((a: VolAssignment) => (
                    <Table.Tr key={a.id}>
                      <Table.Td>{volunteerName(a.volunteerId)}</Table.Td>
                      <Table.Td>{eventName(a.eventId)}</Table.Td>
                      <Table.Td>{a.shiftDate ? dayjs(a.shiftDate).format('MM/DD/YY') : '—'}</Table.Td>
                      <Table.Td>
                        <Badge size="xs" color={STATUS_COLORS[a.status] ?? 'gray'} tt="capitalize">
                          {a.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>

        {/* Certification Breakdown — Treemap */}
        <Paper p="sm" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="xs" mb="xs">Certifications</Text>
          {(() => {
            const certCounts: Record<string, number> = {};
            volunteers.forEach(v => {
              if (v.certs) v.certs.split(',').forEach(c => {
                const t = c.trim(); if (t) certCounts[t] = (certCounts[t] || 0) + 1;
              });
            });
            const sorted = Object.entries(certCounts).sort((a, b) => b[1] - a[1]).slice(0, 12);
            if (sorted.length === 0) return <Text size="xs" c="dimmed" ta="center" py="sm">No certifications recorded.</Text>;
            const total = sorted.reduce((s, [, c]) => s + c, 0);
            const colors = ['#4B3FFF', '#2C01D6', '#19009B', '#0E6F7F', '#037730', '#885604', '#D33423', '#546574', '#7c3aed', '#0891b2', '#d97706', '#16a34a'];
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1, alignContent: 'stretch' }}>
                {sorted.map(([cert, count], i) => {
                  const pct = (count / total) * 100;
                  const bg = colors[i % colors.length];
                  return (
                    <div key={cert} style={{
                      flexBasis: `${Math.max(pct, 15)}%`, flexGrow: 1, minWidth: 90,
                      background: bg, borderRadius: 8, padding: '12px 14px',
                      display: 'flex', flexDirection: 'column', justifyContent: 'center',
                      color: '#fff', cursor: 'default',
                    }} title={`${cert}: ${count} volunteers`}>
                      <Text fz={24} fw={800} c="white" lh={1}>{count}</Text>
                      <Text size="xs" c="rgba(255,255,255,0.85)" fw={500} mt={2} lineClamp={1}>{cert}</Text>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </Paper>
      </SimpleGrid>
    </div>
  );
}
